import React, { useEffect, useState } from 'react';
import { Loader2, Phone, Mail, Users, CalendarDays, Search, BellRing } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import api, { errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const STATUS = [
  { id: 'new', label: 'New', cls: 'bg-amber-100 text-amber-800' },
  { id: 'called', label: 'Called', cls: 'bg-sky-100 text-sky-800' },
  { id: 'registered', label: 'Registered', cls: 'bg-emerald-100 text-emerald-800' },
  { id: 'not_interested', label: 'Not interested', cls: 'bg-stone-200 text-stone-700' },
];
const selectCls = 'bg-white rounded-full border border-rose/50 px-4 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany';

const EnquiryRow = ({ e, onStatus }) => (
  <div className="p-4 rounded-2xl bg-white/70 border border-rose/40 flex flex-col md:flex-row md:items-center gap-3" data-testid={`enquiry-${e.id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-burgundy" data-testid={`enquiry-name-${e.id}`}>{e.name}</span>
        <span className="text-[11px] text-mahogany/60">{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
      </div>
      <div className="text-xs text-rose-italic mt-0.5" data-testid={`enquiry-event-${e.id}`}>{e.program_title}</div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-mahogany/80">
        {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-burgundy" data-testid={`enquiry-phone-${e.id}`}><Phone className="w-3.5 h-3.5" />{e.phone}</a>}
        <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-burgundy"><Mail className="w-3.5 h-3.5" />{e.email}</a>
        {e.num_people && <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{e.num_people}</span>}
        {e.preferred_dates && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{e.preferred_dates}</span>}
      </div>
      {e.message && <p className="mt-2 text-sm text-burgundy/80 whitespace-pre-line">{e.message}</p>}
    </div>
    <div className="flex flex-wrap gap-1.5 md:justify-end">
      {STATUS.map((s) => (
        <button key={s.id} onClick={() => onStatus(e, s.id)} data-testid={`enquiry-status-${s.id}-${e.id}`}
          className={`text-xs px-3 py-1.5 rounded-full transition-colors ${e.status === s.id ? s.cls + ' font-semibold ring-1 ring-current' : 'bg-cream text-mahogany/60 hover:bg-rose-soft'}`}>
          {s.label}
        </button>
      ))}
    </div>
  </div>
);

const AlertSettings = ({ toast }) => {
  const [cfg, setCfg] = useState(null);
  const [phones, setPhones] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/enquiries/settings').then((r) => { setCfg(r.data); setPhones((r.data.phones || []).join(', ')); }).catch(() => {}); }, []);
  if (!cfg) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/enquiries/settings', { ...cfg, phones: phones.split(',').map((p) => p.trim()).filter(Boolean) });
      setCfg(r.data); setPhones(r.data.phones.join(', '));
      toast({ title: 'Alert settings saved' });
    } catch (e) { toast({ title: 'Save failed', description: errText(e) }); } finally { setSaving(false); }
  };
  return (
    <div className="p-5 rounded-2xl bg-white/70 border border-rose/40 space-y-3" data-testid="enquiry-alert-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-burgundy font-medium"><BellRing className="w-4 h-4" /> Text alerts for new requests</div>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="enquiry-alert-enabled" />
      </div>
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <input value={phones} onChange={(e) => setPhones(e.target.value)} placeholder="+1 214 395 6757, +1 …" data-testid="enquiry-alert-phones"
          className={`${selectCls} flex-1`} />
        <label className="flex items-center gap-2 text-sm text-mahogany/80">
          <Switch checked={cfg.notify_organiser} onCheckedChange={(v) => setCfg({ ...cfg, notify_organiser: v })} data-testid="enquiry-alert-organiser" />
          Also text the event organiser
        </label>
        <button onClick={save} disabled={saving} data-testid="enquiry-alert-save" className="pill-btn !py-2 text-sm">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
};

const AdminEnquiries = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ items: [], counts: {}, events: [] });
  const [loading, setLoading] = useState(true);
  const [programId, setProgramId] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/enquiries', { params: { program_id: programId || undefined, status: status || undefined, q: q || undefined } });
      setData(r.data);
    } catch (e) { toast({ title: 'Could not load requests', description: errText(e) }); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [programId, status, q]);

  const setEnqStatus = async (e, s) => {
    try {
      await api.patch(`/admin/enquiries/${e.id}`, { status: s });
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === e.id ? { ...x, status: s } : x)) }));
      load();
    } catch (err) { toast({ title: 'Update failed', description: errText(err) }); }
  };

  return (
    <div className="space-y-6" data-testid="admin-enquiries-page">
      <div>
        <h1 className="font-serif-display text-4xl text-burgundy">Registration <span className="font-italic-display text-rose-italic">Requests</span></h1>
        <p className="mt-2 text-mahogany/70 text-sm">Requests from phone-registration events. Call each person back and mark their progress.</p>
      </div>
      <AlertSettings toast={toast} />
      <div className="flex flex-wrap gap-2" data-testid="enquiry-counts">
        {STATUS.map((s) => (
          <button key={s.id} onClick={() => setStatus(status === s.id ? '' : s.id)} data-testid={`enquiry-count-${s.id}`}
            className={`px-4 py-2 rounded-2xl text-sm ${s.cls} ${status === s.id ? 'ring-2 ring-mahogany' : ''}`}>
            {s.label} <b className="ml-1">{data.counts?.[s.id] || 0}</b>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <select value={programId} onChange={(e) => setProgramId(e.target.value)} className={selectCls} data-testid="enquiry-event-filter">
          <option value="">All events</option>
          {data.events.filter((ev) => ev.id).map((ev) => <option key={ev.id} value={ev.id}>{ev.title} ({ev.count})</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} data-testid="enquiry-status-filter">
          <option value="">All statuses</option>
          {STATUS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-mahogany/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone…" data-testid="enquiry-search"
            className={`${selectCls} pl-9 w-64`} />
        </div>
      </div>
      {loading && !data.items.length ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div>
        : data.items.length === 0 ? <div className="py-16 text-center text-mahogany/60" data-testid="enquiries-empty">No registration requests match.</div>
          : <div className="space-y-3">{data.items.map((e) => <EnquiryRow key={e.id} e={e} onStatus={setEnqStatus} />)}</div>}
    </div>
  );
};

export default AdminEnquiries;
